/**
 * @file Phone helper.
 */

const parsePhone = phone => {
  return (phone.match(/\d/g) || []).join('');
};

/**
 * Validates phone number based on regex found in https://stackoverflow.com/questions/4338267/validate-phone-number-with-javascript.
 *
 * @param {string} phone Phone number to validate.
 * @returns {boolean}
 */
const isValidPhone = phone => {
  return (
    !!phone &&
    phone.length > 7 &&
    // eslint-disable-next-line no-useless-escape
    /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,8}$/im.test(phone)
  );
};

/**
 * Returns whether a phone input holds no actual number.
 *
 * The phone input seeds itself with a country code, so an untouched field
 * reads as "+1" rather than as an empty string.
 *
 * @param {string} phone Phone number to check.
 * @returns {boolean}
 */
const isBlankPhone = phone =>
  !phone || (/^\+\d+$/.test(phone) && phone.length < 7);

const formatPhone = phone => {
  if (!phone) {
    return '';
  }

  const phoneClean = phone.replace('+', '');

  if (phoneClean[0] === '1' && phoneClean.length === 11) {
    return phoneClean
      .match(/\d/g)
      .reduce((p, n) => p.replace(/X/, n), '+X XXX XXX XXXX')
      .replace(/X.*$/g, '')
      .trim();
  }

  return `+${phoneClean}`;
};

const formatPhoneForSearch = phone => {
  if (!phone) {
    return '';
  }

  return (phone.match(/\d/g) || []).join('');
};

/**
 * Normalizes a customer search term. Terms that look like a phone number get
 * their formatting stripped, so that a number copied out of a report (i.e.
 * "+1 555 555 5555") still matches the stored one.
 *
 * @param {string} search Search term to normalize.
 * @returns {string}
 */
const formatCustomerSearch = search => {
  const trimmedSearch = (search ?? '').trim();

  if (!trimmedSearch || !/^[+\s\d().-]+$/.test(trimmedSearch)) {
    return trimmedSearch;
  }

  return formatPhoneForSearch(trimmedSearch);
};

export {
  parsePhone,
  isValidPhone,
  isBlankPhone,
  formatPhone,
  formatPhoneForSearch,
  formatCustomerSearch,
};
